"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useBuyForm = void 0;
var bn_js_1 = __importDefault(require("bn.js"));
var react_1 = require("react");
var _launchpadConstants_1 = require("../../../launchpadConstants");
var _shared_1 = require("../../../shared");
var utils_1 = require("../../../shared/utils");
var hooks_1 = require("../../hooks");
var types_1 = require("../../types");
var useValidation_1 = require("./useValidation");
// TODO: rework both sale and this: to use "currency" as the main currency for calculation, not tokens. (user allocs should be in currency too)
function useBuyForm(_a) {
    var _b;
    var gasPrice = _a.gasPrice, referral = _a.referral, _c = _a.saleType, saleType = _c === void 0 ? 'normal' : _c;
    var _d = (0, _shared_1.useUser)(), account = _d.account, status = _d.kyc.status;
    var _e = (0, hooks_1.usePool)(), pool = _e.pool, saleInstance = _e.saleInstance, isPoolLoading = _e.isLoading, refreshPool = _e.refresh, isSoldOut = _e.isSoldOut, userState = _e.userState, refreshUser = _e.refreshUser;
    var sale = pool.sale, fundToken = pool.fundToken;
    var initCurrencyValue = ((_b = sale.limits) === null || _b === void 0 ? void 0 : _b.min) ? (0, utils_1.toEth)(sale.limits.min) : '0';
    var tokensLeft = sale.tokensForSale.sub(sale.tokensSold);
    var _f = (0, hooks_1.usePriceConverters)(), tokensToCurrency = _f.tokensToCurrency, currencyToTokens = _f.currencyToTokens;
    var _g = (0, hooks_1.useUserCurrencyBalance)(), userCurrencyBalance = _g[0], updateCurrencyBalance = _g[1];
    var _h = (0, react_1.useState)(initCurrencyValue), currencyValue = _h[0], setCurrencyValue = _h[1];
    var _j = (0, react_1.useState)(''), tokenValue = _j[0], setTokenValue = _j[1];
    var balanceWithTolerance = userState.balance.add((0, utils_1.toWeiBN)('0.0001'));
    var _k = (0, react_1.useState)(false), isLoading = _k[0], setIsLoading = _k[1];
    var validationErrors = (0, useValidation_1.useValidation)({
        currencyValue: currencyValue,
        tokenValue: tokenValue,
        userCurrencyBalance: userCurrencyBalance,
        tokensBalance: balanceWithTolerance,
        userCurrencyContribution: userState.contributed,
        tierAllocation: userState.levelAlloc,
        wholeTokens: sale.wholeTokens || false,
    });
    var canBuy = userState.totalAlloc.gtn(0);
    function isMaxReached() {
        return balanceWithTolerance.gte(userState.totalAlloc);
    }
    var isUserReachedLimit = isMaxReached();
    var isDisabled = isPoolLoading ||
        sale.status !== types_1.SaleStatus.open ||
        !account ||
        !saleInstance ||
        tokensLeft.eq(_launchpadConstants_1.ZERO_BN) ||
        tokenValue === '0' ||
        parseFloat(tokenValue) === 0 ||
        validationErrors.currencyError ||
        validationErrors.tokenError ||
        isUserReachedLimit ||
        (pool.kyc && status !== 'valid') ||
        !canBuy;
    /** Effects */
    (0, react_1.useEffect)(function () {
        if (currencyValue) {
            setTokenValue((0, utils_1.toEth)(currencyToTokens((0, utils_1.toWeiBN)(currencyValue)), 8));
        }
    }, [currencyToTokens]);
    /** Methods */
    function onTokenValueChange(e) {
        var value = (0, utils_1.normalize)(e.currentTarget.value);
        if (sale.wholeTokens && value) {
            value = String(Math.floor(parseFloat(value)));
        }
        setTokenValue(value);
        var currencies = value === '' ? '' : (0, utils_1.toEth)(tokensToCurrency((0, utils_1.toWeiBN)(value)), 8);
        setCurrencyValue(currencies);
    }
    function onCurrencyValueChange(e) {
        var value = (0, utils_1.normalize)(e.currentTarget.value);
        setCurrencyValue(value);
        var tokens = value === '' ? '' : (0, utils_1.toEth)(currencyToTokens((0, utils_1.toWeiBN)(value)), 8);
        setTokenValue(tokens);
    }
    function buyTokens() {
        var wei = (0, utils_1.toWeiBN)(currencyValue);
        if (wei.eqn(0) ||
            validationErrors.currencyError ||
            validationErrors.tokenError ||
            isLoading) {
            return;
        }
        setIsLoading(true);
        var buyWithNetworkToken = function () {
            return saleInstance.methods
                .buyTokens()
                .send({ value: wei, from: account, gasPrice: gasPrice });
        };
        var buyWithERC20Token = function () {
            return saleInstance.methods
                .buyTokens((0, utils_1.denormalizeDecimals)(wei, fundToken.decimals))
                .send({ from: account, gasPrice: gasPrice });
        };
        var buyWithReferralNetwork = function () {
            return saleInstance.methods
                .buyTokens(referral || _launchpadConstants_1.ZERO_ADDRESS)
                .send({ value: wei, from: account, gasPrice: gasPrice });
        };
        var buyWithReferralERC20 = function () {
            return saleInstance.methods
                .buyTokens((0, utils_1.denormalizeDecimals)(wei, fundToken.decimals), referral || _launchpadConstants_1.ZERO_ADDRESS)
                .send({ from: account, gasPrice: gasPrice });
        };
        var method = fundToken.address === ''
            ? saleType === 'referral'
                ? buyWithReferralNetwork()
                : buyWithNetworkToken()
            : saleType === 'referral'
                ? buyWithReferralERC20()
                : buyWithERC20Token();
        return method
            .then(function (res) {
            setCurrencyValue('0');
            setTokenValue('0');
            refreshUser();
            updateCurrencyBalance();
            refreshPool();
            return {
                amount: tokenValue,
                transactionHash: res.transactionHash,
            };
        })
            .catch(function (err) {
            return { error: (err === null || err === void 0 ? void 0 : err.message) || err.toString(), msg: 'Failed to buy' };
        })
            .finally(function () {
            setIsLoading(false);
        });
    }
    function getMaxAvailableTokens() {
        if (tokensLeft.eqn(0)) {
            return _launchpadConstants_1.ZERO_BN;
        }
        return bn_js_1.default.min(tokensLeft, bn_js_1.default.min(userState.totalAlloc.sub(userState.balance), currencyToTokens(userCurrencyBalance)));
    }
    function getMaxAvailableCurrency() {
        if (tokensLeft.eqn(0)) {
            return _launchpadConstants_1.ZERO_BN;
        }
        return bn_js_1.default.min(tokensToCurrency(tokensLeft), bn_js_1.default.min(tokensToCurrency(userState.totalAlloc.sub(userState.balance)), userCurrencyBalance));
    }
    function setMaxTokens() {
        var tokens = getMaxAvailableTokens();
        setTokenValue((0, utils_1.toEth)(tokens, 16));
        setCurrencyValue((0, utils_1.toEth)(tokensToCurrency(tokens), 16));
    }
    return {
        // general
        isLoading: isLoading,
        isDisabled: isDisabled,
        validationErrors: validationErrors,
        userWhitelisted: userState.isWhitelisted,
        isSoldOut: isSoldOut,
        userCurrencyBalance: userCurrencyBalance,
        userContribution: userState.contributed,
        userPoolBalance: userState.balance,
        isUserReachedLimit: isUserReachedLimit,
        // tier
        tierAllowsToBuy: userState.levelAlloc.gtn(0),
        // inputs
        currencyValue: currencyValue,
        tokenValue: tokenValue,
        // functions
        onCurrencyValueChange: onCurrencyValueChange,
        onTokenValueChange: onTokenValueChange,
        getMaxAvailableTokens: getMaxAvailableTokens,
        getMaxAvailableCurrency: getMaxAvailableCurrency,
        setMaxTokens: setMaxTokens,
        buyTokens: buyTokens,
    };
}
exports.useBuyForm = useBuyForm;
